import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { NikoProjectExtended } from '../../types/project';
import { useCursorContext } from '../../hooks/useCursorContext';

interface WorkCardProps {
  project: NikoProjectExtended;
  index: number;
}

export function WorkCard({ project, index }: WorkCardProps) {
  const { setCursorState, setCursorText } = useCursorContext();

  const handleHover = (hovering: boolean) => {
    if (hovering) {
      setCursorState('text');
      setCursorText('Öffnen');
    } else {
      setCursorState('default');
      setCursorText('');
    }
  };

  const getProjectImage = () => {
    const colors: Record<string, string> = {
      'Software & KI': '8b5cf6',
      Automotive: '1C69D4',
      Energie: '10b981',
      'IT-Security': 'ef4444',
      Chemie: '84cc16',
      Pharma: 'ec4899',
      Healthcare: '06b6d4',
      'E-Commerce': 'f59e0b',
      FinTech: '3b82f6',
      Agentur: '8b5cf6',
      'Neon Product Lab': '10b981',
    };
    const color = project.industry ? colors[project.industry] || '10b981' : '10b981';
    return `https://placehold.co/800x450/${color}/0a0a0a?text=${encodeURIComponent(project.company)}`;
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.5 }}
    >
      <Link
        to={`/project/${project.id}`}
        className="group block"
        onMouseEnter={() => handleHover(true)}
        onMouseLeave={() => handleHover(false)}
      >
        {/* Image */}
        <div className="relative aspect-video overflow-hidden bg-white/5 rounded-lg">
          <img
            src={getProjectImage()}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Number */}
          <span className="absolute top-3 left-3 text-xs text-white/40 tracking-wider font-mono">
            {String(index + 1).padStart(2, '0')}
          </span>

          {/* Current Badge */}
          {project.current && (
            <span className="absolute top-3 right-3 px-2 py-1 text-[10px] bg-emerald-500/90 text-black rounded tracking-wider uppercase font-medium">
              Aktuell
            </span>
          )}

          {/* Hover Arrow */}
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-full">
              <ArrowUpRight className="w-5 h-5 text-black" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mt-4 space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg md:text-xl font-light text-white group-hover:text-emerald-500 transition-colors line-clamp-2">
                {project.title}
              </h3>
              <p className="text-sm text-white/40 mt-1">{project.company}</p>
            </div>
          </div>

          {/* Industry & Period */}
          <div className="flex items-center gap-3">
            {project.industry && (
              <span className="text-xs text-emerald-500/80">{project.industry}</span>
            )}
            <span className="text-white/20">•</span>
            <span className="text-xs text-white/30">{project.period}</span>
          </div>

          {/* Tech Tags */}
          <div className="flex flex-wrap gap-2 pt-1">
            {project.tech.slice(0, 3).map((tech) => (
              <span
                key={tech}
                className="text-xs text-white/30 bg-white/5 px-2 py-1 rounded"
              >
                {tech}
              </span>
            ))}
            {project.tech.length > 3 && (
              <span className="text-xs text-white/20">+{project.tech.length - 3}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
